<template>
  <div>
    <dl class="eligibility-list mb-0">
      <template v-for="aggregate in aggregates" :key="aggregate.dimension">
        <dt>{{ $t(`forwards.pools.eligibility.${aggregate.label}`) }}</dt>
        <dd :data-dimension="aggregate.dimension">
          <InfoTooltip :text="aggregate.tooltip" :showIcon="false">
            <FontAwesomeIcon
              :icon="
                aggregate.passes
                  ? 'fa-solid fa-circle-check'
                  : 'fa-solid fa-circle-xmark'
              "
              :class="aggregate.passes ? 'text-success' : 'text-body-tertiary'"
              :aria-label="aggregate.tooltip"
              class="me-2"
            />
          </InfoTooltip>
          {{ aggregate.value }}
          <div
            v-if="
              aggregate.dimension === 'countries' &&
              indicator.countries.length > 0
            "
            class="d-flex flex-wrap gap-1 mt-1"
          >
            <CountryPillComponent
              v-for="country in indicator.countries"
              :key="country"
              :country="country"
            />
          </div>
        </dd>
      </template>

      <dt>{{ $t("forwards.pools.eligibility.ecolabels") }}</dt>
      <dd>
        <ul class="list-unstyled mb-0">
          <li
            v-for="ecolabel in indicator.ecolabels"
            :key="ecolabel.key"
            class="d-flex align-items-baseline gap-2"
          >
            <InfoTooltip :text="ecolabelTooltip(ecolabel)" :showIcon="false">
              <FontAwesomeIcon
                :aria-label="ecolabelTooltip(ecolabel)"
                :icon="
                  ecolabel.all_devices_qualify
                    ? 'fa-solid fa-circle-check'
                    : 'fa-solid fa-circle-xmark'
                "
                :class="
                  ecolabel.all_devices_qualify
                    ? 'text-success'
                    : 'text-body-tertiary'
                "
              />
            </InfoTooltip>
            <span>
              {{ ecolabel.label }}
              <span
                v-if="
                  ecolabel.all_devices_qualify &&
                  ecolabel.expires_after_year !== null
                "
                class="text-body-tertiary"
              >
                —
                {{
                  $t("forwards.pools.eligibility.through_year", {
                    year: ecolabel.expires_after_year,
                  })
                }}
              </span>
              <span
                v-else-if="!ecolabel.all_devices_qualify"
                class="text-body-tertiary failing-devices"
              >
                — {{ ecolabelDevices(ecolabel.failing_device_ids) }}
              </span>
            </span>
          </li>
        </ul>
      </dd>
    </dl>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import { formatDate } from "@/composables/formatUtils";
import CountryPillComponent from "@/forwards2/components/CountryPillComponent.vue";
import type {
  EligibilityIndicator,
  PoolDeviceRow,
} from "@/forwards2/forwardsTypes";
import { technologyName } from "@/types/technology";
import { uniq } from "lodash-es";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/** Above this many devices a line states the count alone; the names would fill the card. */
const INLINE_NAME_LIMIT = 3;

/**
 * What a deal restricting each dimension would find in this pool: the worst device sets every value.
 *
 * "OK-power new" expires — a device stops satisfying it once it is
 * older than its technology's limit — so the row names the last production year the pool as a whole
 * still qualifies for, rather than a plain yes.
 */
const props = defineProps<{
  indicator: EligibilityIndicator;
  /**
   * The devices the indicator was computed over — the pool's covered set, not every candidate — so
   * each line can state its device count as a fraction of the pool. Also supplies the names of the
   * few devices a short line still spells out.
   */
  devices: PoolDeviceRow[];
}>();

const { t, te } = useI18n();

const nameById = computed(
  () =>
    new Map(
      props.devices.map((device) => [device.device_id, device.device_name])
    )
);

/**
 * What every line counts against. The indicator arrives from a debounced preview, so it can still
 * name a device the form has just dropped; counting those in keeps the fraction from reading
 * "3 of 2 devices" until the next preview lands (D-FIX5-1-02).
 */
const totalDevices = computed(
  () =>
    uniq([
      ...props.devices.map((device) => device.device_id),
      ...props.indicator.ecolabels.flatMap(
        (ecolabel) => ecolabel.failing_device_ids
      ),
      ...props.indicator.blockers.flatMap((blocker) => blocker.device_ids),
    ]).length
);

const technologyMix = computed(() => {
  if (props.indicator.technology_mix.length === 0)
    return t("forwards.common.unknown");
  return props.indicator.technology_mix.map(technologyName).join(", ");
});

/** The server judges the preference (`eligibility_service.CodPreference`); the card only explains it. */
const codPreference = computed(() => {
  const { claim_year, cutoff, passes, older_device_ids } =
    props.indicator.cod_preference;
  const missing = props.indicator.blockers.filter(
    (blocker) => blocker.dimension === "cod"
  );
  const explanation = t("forwards.pools.eligibility.cod_claim_year", {
    year: claim_year,
    cutoff: formatDate(cutoff),
  });
  const reasons = missing.map(
    (blocker) => `${blocker.detail} — ${blockerDevices(blocker.device_ids)}`
  );
  if (older_device_ids.length > 0)
    reasons.push(failingDevices(older_device_ids));
  return {
    passes,
    tooltip: [
      explanation,
      t(
        `forwards.pools.eligibility.${passes ? "cod_preferred" : "cod_not_preferred"}`
      ),
      ...reasons,
      ...(passes ? [] : [t("forwards.pools.eligibility.cod_standards_note")]),
    ].join(" "),
  };
});

const aggregates = computed(() =>
  [
    {
      dimension: "countries",
      label: "country_tier",
      value: t(
        `forwards.pools.eligibility.tier.${props.indicator.country_tier}`
      ),
    },
    {
      dimension: "technologies",
      label: "technology_mix",
      value: technologyMix.value,
    },
    {
      dimension: "cod",
      label: "oldest_cod",
      value: props.indicator.oldest_cod
        ? formatDate(props.indicator.oldest_cod)
        : t("forwards.common.unknown"),
    },
    {
      dimension: "support",
      label: "supported",
      value: t(`forwards.common.support.${props.indicator.support}`),
    },
  ].map((aggregate) => {
    if (aggregate.dimension === "cod")
      return { ...aggregate, ...codPreference.value };
    const blockers = props.indicator.blockers.filter(
      (blocker) =>
        blocker.dimension === aggregate.dimension ||
        (aggregate.dimension === "support" &&
          blocker.dimension === "support_unknown")
    );
    const passes = totalDevices.value > 0 && blockers.length === 0;
    return {
      ...aggregate,
      passes,
      tooltip:
        blockers.length > 0
          ? blockers
              .map(
                (blocker) =>
                  `${blocker.detail} — ${blockerDevices(blocker.device_ids)}`
              )
              .join("; ")
          : t(
              `forwards.pools.eligibility.${passes ? "no_issues" : "no_devices"}`
            ),
    };
  })
);

/**
 * The verdict, then what the ecolabel requires of each device (`eligibility_service.ecolabel_matches`);
 * an ecolabel added by the backend after this file was written gets the verdict alone. A device's
 * `ecolabel_approved` flag is its EKOenergy pre-approval, named before other labels were tracked;
 * OK-power compatibility is Soldera's own assessment, built on that pre-approval.
 */
function ecolabelTooltip(
  ecolabel: EligibilityIndicator["ecolabels"][number]
): string {
  const verdict = ecolabel.all_devices_qualify
    ? ecolabel.expires_after_year === null
      ? t("forwards.pools.eligibility.all_qualify")
      : t("forwards.pools.eligibility.through_year", {
          year: ecolabel.expires_after_year,
        })
    : ecolabelDevices(ecolabel.failing_device_ids);
  const requirementKey = `forwards.pools.eligibility.ecolabel_requirements.${ecolabel.key}`;
  const summary = `${ecolabel.label} — ${verdict}`;
  return te(requirementKey) ? `${summary}. ${t(requirementKey)}` : summary;
}

function failingDevices(deviceIds: number[]): string {
  return deviceSummary(
    deviceIds,
    "forwards.pools.eligibility.failing_device_count",
    "forwards.pools.eligibility.failing_device_count_named"
  );
}

/** Ecolabel lines state the count alone: a pool can carry many labels, each failing on many devices. */
function ecolabelDevices(deviceIds: number[]): string {
  return t("forwards.pools.eligibility.failing_device_count", {
    count: deviceIds.length,
    total: totalDevices.value,
  });
}

function blockerDevices(deviceIds: number[]): string {
  return deviceSummary(
    deviceIds,
    "forwards.pools.eligibility.blocker_device_count",
    "forwards.pools.eligibility.blocker_device_count_named"
  );
}

/**
 * "12 of 40 devices", naming them only while the list is short enough to read at a glance — a pool
 * of a hundred devices otherwise turns every line of this card into a wall of names. Ecolabel lines
 * never name devices (user decision 2026-09-17).
 */
function deviceSummary(
  deviceIds: number[],
  countKey: string,
  namedKey: string
): string {
  const counts = { count: deviceIds.length, total: totalDevices.value };
  if (deviceIds.length === 0 || deviceIds.length > INLINE_NAME_LIMIT)
    return t(countKey, counts);
  return t(namedKey, { ...counts, devices: deviceNames(deviceIds) });
}

function deviceNames(deviceIds: number[]): string {
  return deviceIds
    .map(
      (id) =>
        nameById.value.get(id) ?? t("forwards.pools.devices.unnamed", { id })
    )
    .join(", ");
}
</script>

<style scoped lang="scss">
.eligibility-list {
  display: grid;
  grid-template-columns: minmax(7rem, auto) 1fr;
  gap: 0.35rem 0.75rem;
  font-size: 0.8125rem;
  margin-bottom: 0;

  dt {
    font-weight: 600;
    color: var(--phoenix-gray-600);
  }

  dd {
    margin-bottom: 0;
  }
}
</style>
